import styled, { css, keyframes } from 'styled-components';

const MOBILE = '@media (max-width: 768px)';
const CARD_WIDTH_DESKTOP = '40rem';
const CARD_WIDTH_MOBILE = '32rem';
const AFTER_CELL_STAGGER_SECONDS = 0.03;
const STAT_FADE_DELAY_SECONDS = 0.6;

const cellPop = keyframes`
  from {
    opacity: 0;
    transform: scale(0.4);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(0.6rem);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const CompareCard = styled.div`
  width: ${CARD_WIDTH_DESKTOP};
  background-color: ${({ theme }) => theme.colors.white};
  border: 0.1rem solid ${({ theme }) => theme.colors.border};
  border-radius: 2rem;
  padding: 2rem 1.8rem;
  box-shadow:
    0 1.6rem 3.2rem rgba(15, 23, 42, 0.08),
    0 0 0 0.1rem rgba(15, 23, 42, 0.02);

  ${MOBILE} {
    width: ${CARD_WIDTH_MOBILE};
    padding: 1.6rem 1.2rem;
  }
`;

export const CompareTitle = styled.div`
  font-size: 1.1rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.slateGrey};
  text-align: center;
  margin-bottom: 1.6rem;
  text-transform: uppercase;
  letter-spacing: 0.12rem;
`;

export const CompareGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: stretch;
  gap: 1rem;

  ${MOBILE} {
    gap: 0.6rem;
  }
`;

export const SideColumn = styled.div<{ $variant: 'before' | 'after' }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.2rem;
  padding: 1.4rem 1rem;
  border-radius: 1.4rem;
  background-color: ${({ $variant, theme }) =>
    $variant === 'after' ? theme.colors.accentBgSoft : theme.colors.white};
  border: 0.1rem solid
    ${({ $variant, theme }) =>
      $variant === 'after' ? theme.colors.accentBorder : theme.colors.border};

  ${MOBILE} {
    padding: 1rem 0.6rem;
    gap: 0.9rem;
  }
`;

export const SideLabel = styled.div<{ $variant: 'before' | 'after' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.9rem;
  border-radius: 999rem;
  font-size: 1rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.1rem;
  color: ${({ $variant, theme }) =>
    $variant === 'after' ? theme.colors.white : theme.colors.slateGrey};
  background-color: ${({ $variant, theme }) =>
    $variant === 'after' ? theme.colors.accent : theme.colors.border};
`;

export const SideSub = styled.span`
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.slateGrey};
  margin-top: -0.4rem;
`;

export const MonthGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.3rem;
  width: 100%;
`;

export const MonthCell = styled.div<{
  $booked: boolean;
  $variant: 'before' | 'after';
  $index: number;
  $active: boolean;
}>`
  aspect-ratio: 1;
  border-radius: 0.3rem;
  background-color: ${({ $booked, $variant, theme }) => {
    if (!$booked) {
      return $variant === 'after'
        ? theme.colors.white
        : theme.colors.border;
    }
    return $variant === 'after'
      ? theme.colors.accent
      : theme.colors.borderStrong;
  }};
  ${({ $booked, $variant, $index, $active }) =>
    $active && $booked && $variant === 'after'
      ? css`
          animation: ${cellPop} 0.4s ease-out backwards;
          animation-delay: ${$index * AFTER_CELL_STAGGER_SECONDS}s;
        `
      : ''}
`;

export const StatBlock = styled.div<{
  $variant: 'before' | 'after';
  $active: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
  ${({ $variant, $active }) =>
    $active
      ? css`
          animation: ${slideUp} 0.5s ease-out backwards;
          animation-delay: ${$variant === 'after'
            ? STAT_FADE_DELAY_SECONDS
            : 0}s;
        `
      : ''}
`;

export const StatValue = styled.span<{ $variant: 'before' | 'after' }>`
  font-size: 2.4rem;
  font-weight: 800;
  letter-spacing: -0.06rem;
  color: ${({ $variant, theme }) =>
    $variant === 'after' ? theme.colors.accentTextDark : theme.colors.darkSlate};

  ${MOBILE} {
    font-size: 2rem;
  }
`;

export const StatLabel = styled.span`
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.slateGrey};
  text-transform: uppercase;
  letter-spacing: 0.08rem;
`;

export const StatStars = styled.div<{ $variant: 'before' | 'after' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 1.4rem;
  font-weight: 800;
  color: ${({ $variant, theme }) =>
    $variant === 'after' ? theme.colors.accentTextDark : theme.colors.darkSlate};
`;

export const StarIcon = styled.span<{ $variant: 'before' | 'after' }>`
  color: ${({ $variant, theme }) =>
    $variant === 'after' ? theme.colors.amberSolid : theme.colors.borderStrong};
`;

export const ArrowCol = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.accent};

  ${MOBILE} {
    font-size: 1.6rem;
  }
`;

export const CompareFootnote = styled.div`
  margin-top: 1.4rem;
  font-size: 1.05rem;
  color: ${({ theme }) => theme.colors.slateGrey};
  text-align: center;
  line-height: 1.4;
`;

export const FootnoteAccent = styled.span`
  color: ${({ theme }) => theme.colors.accentTextDark};
  font-weight: 700;
`;
